import React, { useState, useEffect, useRef } from 'react';
import { ListButton, CreateButton, SaveButton, DeleteButton, Toolbar, ReferenceInput, AutocompleteInput, Form, useGetList, useSidebarState, useUpdate, useGetIdentity, useLocaleState, useTranslate, useGetResourceLabel } from 'react-admin';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import moment from 'moment';

import Breadcrumb from '../common/layout/Breadcrumb';
import HelpButton from './buttons/HelpButton';
import TimeEntryEdit from './TimeEntryEdit';
import TimeEntryBulkCreate from './TimeEntryBulkCreate';

import time_entries from '.';

import { stringToColor } from '../../services/utils';

const userOptionText = choice => choice?.fullname ? choice.fullname : '';

const formSx = {
    '& .MuiPaper-root': { border: 'none' }
};
const formUserSx = {
    minWidth: '200px',
    position: 'absolute',
    right: 0,
    '& .RaAutocompleteInput-textField': {
        minWidth: '200px'
    }
};
const cardSx = (theme) => ({
    position: 'relative',
    marginTop: '15px',
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    height: 'calc(100vh - 150px)',
    marginBottom: '10px',
    '& .fc-toolbar-title': {
        fontSize: theme.typography.h5.fontSize,
        fontWeight: theme.typography.h5.fontWeight,
    },
    '& .fc-button': {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.action.active,
        borderColor: theme.palette.divider,
        fontSize: theme.typography.button.fontSize,
        fontWeight: theme.typography.button.fontWeight,
        textTransform: theme.typography.button.textTransform,
    },
    '& .fc-button-primary:disabled': {
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.action.selected,
        color: theme.palette.action.active,
        '&:hover': {
            backgroundColor: theme.palette.action.selected,
            color: theme.palette.action.active
        }
    },
    '& .fc-button-group .fc-button': {
        borderColor: theme.palette.divider,
        color: theme.palette.action.active,
    },
    '& .fc-button:hover': {
        borderColor: theme.palette.divider,
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
    },
    '& .fc-button-primary:not(:disabled):active': {
        backgroundColor: theme.palette.action.active,
        color: theme.palette.text.primary,
    },
    '& .fc-button-group .fc-button-primary:not(:disabled):active': {
        borderColor: theme.palette.divider,
    },
    '& .fc-button-primary:not(:disabled).fc-button-active': {
        backgroundColor: theme.palette.action.selected,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        color: theme.palette.action.active,
    },
    '& .fc-button-group .fc-button-primary:not(:disabled).fc-button-active': {
        borderColor: theme.palette.divider,
        color: theme.palette.text.secondary,
    }
});

const CustomToolbar = ({ onDelete, ...props }) => (
    <Toolbar {...props}>
        <SaveButton />
        <DeleteButton
            mutationMode="pessimistic"
            mutationOptions={{ onSuccess: onDelete }}
        />
    </Toolbar>
);

const TimeEntryCalendar = ({ sx, ...props }) => {
    const [userId, setUserId] = useState();
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const [showDialogCreate, setShowDialogCreate] = useState(false);
    const [showDialogEdit, setShowDialogEdit] = useState(false);
    const [currentId, setCurrentId] = useState();
    const [defaultValuesTimeEntry, setDefaultValuesTimeEntry] = useState();
    const [sidebarIsOpen] = useSidebarState();
    const calendarRef = useRef(null);
    const { data: account } = useGetIdentity();
    const [updateTimeEntry] = useUpdate();
    const [locale] = useLocaleState();
    const translate = useTranslate();
    const getResourceLabel = useGetResourceLabel();
    const { data } = useGetList(
        'time_entries',
        {
            pagination: { page: 1, perPage: 300 },
            sort: { field: 'id', order: 'ASC' },
            filter: { start_date: startDate, end_date: endDate, user_id: userId || account?.id }
        }
    );

    const isFirstRender = useRef(true);
    useEffect(() => {
        isFirstRender.current = false;
    }, []);

    useEffect(() => {
        if (!isFirstRender.current) {
            setTimeout(() => {
                if (calendarRef.current) {
                    calendarRef.current.getApi().updateSize();
                }
            }, 300);
        }
    }, [sidebarIsOpen]);

    const handleOpenCreate = (defaultValues) => {
        setShowDialogCreate(true);
        setDefaultValuesTimeEntry(defaultValues);
    };

    const handleCloseCreate = () => {
        setShowDialogCreate(false);
        setDefaultValuesTimeEntry(null);
    };

    const handleOpenEdit = (id) => {
        setShowDialogEdit(true);
        setCurrentId(id);
    };

    const handleCloseEdit = () => {
        setShowDialogEdit(false);
        setCurrentId(null);
    };

    const convertToEvent = (record) => {
        const color = stringToColor(record?.type?.name, { dark: true });
        const editable = record?.user_id === account?.id;

        return {
            id: record?.id,
            title: record?.description,
            start: record?.date,
            backgroundColor: color,
            borderColor: color,
            editable,
            extendedProps: {
                record,
                editable,
                hours: record?.hours,
                project: record?.project?.name
            }
        };
    };

    const handleDatesSet = (dateInfo) => {
        const start = moment(dateInfo.startStr).format('YYYY-MM-DD');
        const end = moment(dateInfo.endStr).subtract(1, 'days').format('YYYY-MM-DD');

        setStartDate(start);
        setEndDate(end);
    };

    const handleSelect = (selectInfo) => {
        const calendarApi = selectInfo.view.calendar;
        calendarApi.unselect();

        const defaultValues = {
            default_date: selectInfo.startStr,
            time_entries: [{}]
        };

        handleOpenCreate(defaultValues);
    };

    const handleClickEvent = ({ event }) => {
        if (event?.extendedProps?.editable) {
            handleOpenEdit(event.id);
        }
    };

    const handleChangeEvent = async (changeInfo) => {
        if (changeInfo?.event?.extendedProps?.record && changeInfo?.event?.startStr) {
            const payload = {
                id: changeInfo.event.id,
                data: {
                    ...changeInfo.event.extendedProps.record,
                    date: changeInfo?.event?.startStr
                }
            };
            updateTimeEntry('time_entries', payload);
        }
    };

    const renderEventContent = (eventInfo) => {
        const { title } = eventInfo?.event;
        const { hours, project } = eventInfo?.event?.extendedProps;

        const minHours = 0.25;
        const maxHours = 8;
        const minHeight = 30;
        const maxHeight = 400;

        const clampedHours = Math.max(minHours, Math.min(hours, maxHours));
        const ratio = (clampedHours - minHours) / (maxHours - minHours);
        const height = minHeight + ratio * (maxHeight - minHeight);

        return (
            <div
                className="fc-event-title fc-sticky"
                style={{
                    minHeight: height,
                    whiteSpace: 'normal',
                    overflow: 'visible'
                }}
            >
                {project && (<><strong>{project}</strong><br /></>)}
                {title}
            </div>
        );
    };

    const handleChangeUser = (id) => {
        setUserId(id || null);
    };

    const breadcrumbPath = [
        { url: '/time_entries', title: translate('resources.time_entries.name', { smart_count: 2, _: getResourceLabel('time_entries', 2) }), isActive: false, icon: <time_entries.icon /> },
        { url: 'time_entries/calendar', title: translate('Calendar'), isActive: true }
    ];

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Breadcrumb path={breadcrumbPath} />
                <Box>
                    <HelpButton />
                    <CreateButton resource="time_entries" />
                    <ListButton resource="time_entries" />
                </Box>
            </Box>
            <Card sx={{ padding: '20px' }}>
                <Box sx={[cardSx, sx]}>
                    <Box sx={formUserSx}>
                        <Form>
                            <ReferenceInput source="user_id" reference="users" sort={{ field: 'id', order: 'ASC' }} alwaysOn>
                                <AutocompleteInput optionText={userOptionText} size="small" label="User" onChange={handleChangeUser} />
                            </ReferenceInput>
                        </Form>
                    </Box>
                    <FullCalendar
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                        initialView="dayGridWeek"
                        headerToolbar={{
                            left: 'prev,next today',
                            center: 'title',
                            right: null
                        }}
                        selectable={true}
                        firstDay={1}
                        events={data ? [...data?.map(convertToEvent)] : []}
                        eventContent={renderEventContent}
                        datesSet={handleDatesSet}
                        select={handleSelect}
                        eventClick={handleClickEvent}
                        eventChange={handleChangeEvent}
                        locale={locale === 'fr' ? frLocale : null}
                        ref={calendarRef}
                        hiddenDays={[0, 6]}
                        height="100%"
                        {...props}
                    />
                </Box>
            </Card>

            <Dialog open={showDialogEdit} onClose={handleCloseEdit}>
                {currentId && (
                    <TimeEntryEdit
                        id={currentId}
                        resource="time_entries"
                        actions={false}
                        sx={formSx}
                        redirect={false}
                        mutationOptions={{ onSuccess: handleCloseEdit }}
                        toolbar={<CustomToolbar onDelete={handleCloseEdit} />}
                    />
                )}
            </Dialog>

            <Dialog open={showDialogCreate} onClose={handleCloseCreate} maxWidth="xl">
                <TimeEntryBulkCreate
                    actions={false}
                    showLastTimeEntryList={false}
                    sx={formSx}
                    defaultValues={defaultValuesTimeEntry}
                    redirect={false}
                    mutationOptions={{ onSuccess: handleCloseCreate }}
                />
            </Dialog>
        </>
    );
};

export default TimeEntryCalendar;
